// Packages
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Button,
  Input,
  InputGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from "reactstrap";
import axios from "axios";
import Select from "react-select";
import {
  BsSearch,
  BsFiletypeXlsx,
  BsFillPersonVcardFill,
} from "react-icons/bs";
// Utils
import {
  getRequestHeaders,
  catchHandler,
  collaborationTypesLabels,
  yearsOptions,
  taxOptions,
  entriesToOptions,
  userHasAccess,
  userRoles,
  enumToOptions,
} from "../utils";
// Components
import Pagination from "../components/Pagination";
// Styling
import "./Dashboard.css";
import { statusLabels, statusOptions, taxLabels } from "../utils/constants";

const Dashboard = React.memo((props) => {
  const { state: locationState = {} } = useLocation();
  const { pageNumber = 1 } = locationState;
  const [loading, setLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState(false);
  const [registredUsers, setRegistredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [totalCount, setTotalCount] = useState(0);
  const history = useHistory();
  const catchHandlerBinded = catchHandler.bind(null, history);
  const [searchValue, setSearchValue] = useState("");
  const [searchTriggerValue, setSearchTriggerValue] = useState("");
  const [locationsOptions, setLocationsOptions] = useState([]);
  const [subjectsOptions, setSubjectsOptions] = useState([]);
  const [coursesOptions, setCoursesOptions] = useState([]);
  const collaborationTypesOptions = useMemo(
    () => enumToOptions(collaborationTypesLabels),
    []
  );
  const [filters, setFilters] = useState({
    enrollmentYear: "",
    tax: "",
    location: "",
    subject: "",
    course: "",
    collaborationType: "",
    status: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [activityComment, setActivityComment] = useState("");
  const isAdmin = userHasAccess([userRoles.ADMIN]);

  const getData = useCallback((currentPage, filters, searchTriggerValue) => {
    setLoading(true);

    axios
      .get(
        `/api/dashboard/list-registred-users`,
        getRequestHeaders(
          Object.assign(
            {
              page: currentPage,
              filters,
            },
            searchTriggerValue && {
              search: searchTriggerValue,
            }
          )
        )
      )
      .then((res) => {
        const { data = {} } = res;
        const { items = [], totalCount = 0 } = data;
        setRegistredUsers(items);
        setTotalCount(totalCount);
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getModalData = useCallback((userId) => {
    setModalLoading(true);

    axios
      .get(`/api/dashboard/get-user-data/${userId}`, getRequestHeaders())
      .then((res) => {
        const { data = {} } = res;
        setActivityComment(data.activityComment);
        setModalData(data);
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setModalLoading(false);
      });
  }, []);

  const getOptions = useCallback(() => {
    setLoadingActions(true);
    Promise.all([
      axios.get(`/api/locations/select-locations`, getRequestHeaders({})),
      axios.get(`/api/subjects/select-subjects`, getRequestHeaders({})),
      axios.get(`/api/courses/select-courses`, getRequestHeaders({})),
    ])
      .then(([resLocations, resSubjects, resCourses]) => {
        const [
          { data: dataLocations = [] },
          { data: dataSubjects = [] },
          { data: dataCourses = [] },
        ] = [resLocations, resSubjects, resCourses];
        setLocationsOptions(dataLocations.map(entriesToOptions));
        setSubjectsOptions(dataSubjects.map(entriesToOptions));
        setCoursesOptions(dataCourses.map(entriesToOptions));
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setLoadingActions(false);
      });
  }, []);

  useEffect(() => {
    getData(currentPage, filters, searchTriggerValue);
    getOptions();
  }, [currentPage, filters, searchTriggerValue]);

  const toggleModal = useCallback(
    (userId) => {
      if (typeof userId === "string") {
        getModalData(userId);
      } else {
        setActivityComment("");
        setModalData(null);
      }

      setModalOpen((prevState) => !prevState);
    },
    [getModalData]
  );

  const submitEditActivityComment = useCallback(
    (userId, activityComment) => {
      setLoading(true);

      axios
        .patch(
          `/api/dashboard/edit-activity-comment/${userId}`,
          { activityComment },
          getRequestHeaders()
        )
        .then((res) => {
          const { data = {} } = res;
          const { email = "" } = data;
          toast.success(
            `Comentariile utilizatorului [${email}] au fost modificate cu succes`
          );
          setActivityComment("");
          setModalData(null);
          setModalOpen(false);
        })
        .catch(catchHandlerBinded)
        .finally(() => {
          setLoading(false);
        });
    },
    [history]
  );

  const onActivityCommentChange = useCallback((event) => {
    const { target = {} } = event;
    const { value } = target;
    setActivityComment(value);
  }, []);

  const getRows = useCallback(
    () =>
      registredUsers.length ? (
        registredUsers.map(
          (
            {
              _id,
              membershipId,
              email,
              metadata,
              nationalId,
              phoneNumber,
              taxPaid,
              taxNotApplicable,
              subscriptions,
            },
            id
          ) => (
            <tr key={`registred-users-row-${_id}`}>
              <td>
                {membershipId ? (
                  String(membershipId).padStart(7, "0")
                ) : (
                  <Badge color="secondary" pill>
                    User inactiv
                  </Badge>
                )}
              </td>
              <td>{metadata.Nume}</td>
              <td>{metadata.Prenume}</td>
              <td>{nationalId}</td>
              <td>{email}</td>
              <td>{phoneNumber}</td>
              <td>
                {taxNotApplicable ? (
                  <Badge color="secondary" pill>
                    {taxLabels.NOT_APPLICABLE}
                  </Badge>
                ) : taxPaid ? (
                  <Badge color="success" pill>
                    {taxLabels.PAID}
                  </Badge>
                ) : (
                  <Badge color="danger" pill>
                    {taxLabels.NOT_PAID}
                  </Badge>
                )}
              </td>
              <td>
                {subscriptions.map(({ course }) => course?.name).join(", ")}
              </td>
              <td>
                {subscriptions.map(({ subject }) => subject?.name).join(", ")}
              </td>
              <td>
                {subscriptions.map(({ location }) => location?.name).join(", ")}
              </td>
              <td>
                {subscriptions
                  .map(
                    ({ collaborationType }) =>
                      collaborationTypesLabels[collaborationType]
                  )
                  .join(", ")}
              </td>
              <td>
                {subscriptions
                  .map(({ enrollmentYear }) => enrollmentYear)
                  .join(", ")}
              </td>
              <td>
                {subscriptions
                  .map(({ approved }) => (approved ? "Da" : "Nu"))
                  .join(", ")}
              </td>
              <td>
                {subscriptions
                  .map(({ blocked, graduated }) =>
                    blocked || graduated ? statusLabels.INACTIVE : statusLabels.ACTIVE
                  )
                  .join(", ")}
              </td>
              <td className="text-center">
                <Button
                  disabled={loadingActions}
                  color="primary"
                  size="sm"
                  onClick={toggleModal.bind(null, _id)}
                >
                  <BsFillPersonVcardFill />
                </Button>
              </td>
              <td className="text-center">
                <Button
                  disabled={loadingActions}
                  color="secondary"
                  size="sm"
                  tag={Link}
                  to={`/users/${_id}/documents`}
                >
                  Dosar
                </Button>
              </td>
            </tr>
          )
        )
      ) : (
        <tr>
          <td colSpan={16} className="text-center">
            <span>Fara rezultat</span>
          </td>
        </tr>
      ),
    [registredUsers, loadingActions, toggleModal]
  );

  const onSearchChange = useCallback((event) => {
    const { target = {} } = event;
    const { value } = target;
    setSearchValue(() => value);
  }, []);

  const handleSearch = useCallback(
    (event) => {
      const { type, key } = event;

      if (type === "click" || (type === "keyup" && key === "Enter")) {
        setSearchTriggerValue(searchValue);
        setCurrentPage(1);
      }
    },
    [searchValue]
  );

  const handleFilters = useCallback((option, action) => {
    const { value } = option || {};
    const { name } = action || {};

    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
    setCurrentPage(1);
  }, []);

  const handleExportExcel = useCallback(() => {
    setLoadingActions(true);

    axios
      .get(
        `/api/dashboard/export-to-excel`,
        getRequestHeaders(
          Object.assign(
            { requestBlobFlag: true, filters },
            searchTriggerValue && {
              search: searchTriggerValue,
            }
          )
        )
      )
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Export excel`;
        link.click();
        link.remove();
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setLoadingActions(false);
      });
  }, [filters, searchTriggerValue]);

  return (
    <Container className="mt-5 mb-5" style={{ maxWidth: "95%" }}>
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        size="xl"
        centered={true}
        scrollable={true}
      >
        <ModalHeader toggle={toggleModal}>Detalii utilizator</ModalHeader>
        <ModalBody>
          {modalLoading ? (
            <div className="text-center">
              <Spinner color="dark" style={{ width: "5rem", height: "5rem" }} />
            </div>
          ) : modalData ? (
            <>
              <h5>Detalii personale</h5>
              <Row key="dashboard-modal-row-personal-details-1">
                <Col md={6}>
                  <label>Nume:</label>&nbsp;
                  <span>{modalData.Nume}</span>
                </Col>
                <Col md={6}>
                  <label>Prenume:</label>&nbsp;
                  <span>{modalData.Prenume}</span>
                </Col>
              </Row>
              <Row key="dashboard-modal-row-personal-details-2">
                <Col md={6}>
                  <label>Email:</label>&nbsp;
                  <span>{modalData.Email}</span>
                </Col>
                <Col md={6}>
                  <label>Telefon:</label>&nbsp;
                  <span>{modalData.Telefon}</span>
                </Col>
              </Row>
              {modalData.CetateanStrain ? (
                <Row key="dashboard-modal-row-personal-details-3">
                  <Col md={12}>
                    <label>SSN:</label>&nbsp;
                    <span>{modalData.SSN}</span>
                  </Col>
                </Row>
              ) : (
                <Row key="dashboard-modal-row-personal-details-3">
                  <Col md={6}>
                    <label>CNP:</label>&nbsp;
                    <span>{modalData.CNP}</span>
                  </Col>
                  <Col md={2}>
                    <label>Serie CI:</label>&nbsp;
                    <span>{modalData.Serie}</span>
                  </Col>
                  <Col md={4}>
                    <label>Numar CI:</label>&nbsp;
                    <span>{modalData.Numar}</span>
                  </Col>
                </Row>
              )}
              <Row key="dashboard-modal-row-personal-details-4">
                <Col md={3}>
                  <label>Inaltime:</label>&nbsp;
                  <span>{modalData.Inaltime}cm</span>
                </Col>
                <Col md={3}>
                  <label>Greutate:</label>&nbsp;
                  <span>{modalData.Greutate}kg</span>
                </Col>
              </Row>
              <h5 className="mt-3">Adresa Domiciliu</h5>
              <Row key="dashboard-modal-row-personal-address-1">
                <Col md={4}>
                  <label>Str:</label>&nbsp;
                  <span>{modalData.StradaDomiciliu}</span>
                </Col>
                <Col md={2}>
                  <label>Nr:</label>&nbsp;
                  <span>{modalData.NumarDomiciliu}</span>
                </Col>
                <Col md={2}>
                  <label>Bl:</label>&nbsp;
                  <span>{modalData.BlocDomiciliu}</span>
                </Col>
                <Col md={2}>
                  <label>Sc:</label>&nbsp;
                  <span>{modalData.ScaraDomiciliu}</span>
                </Col>
                <Col md={2}>
                  <label>Ap:</label>&nbsp;
                  <span>{modalData.ApartamentDomiciliu}</span>
                </Col>
              </Row>
              <Row key="dashboard-modal-row-personal-address-2">
                <Col md={6}>
                  <label>Judet:</label>&nbsp;
                  <span>{modalData.JudetDomiciliu}</span>
                </Col>
                <Col md={6}>
                  <label>Localitate/Sector:</label>&nbsp;
                  <span>{modalData.LocalitateDomiciliu}</span>
                </Col>
              </Row>
              <h5 className="mt-3">Adresa Resedinta</h5>
              {modalData.AceeasiAdresa ? (
                <span>Aceeasi adresa ca cea de domiciliu</span>
              ) : (
                <>
                  <Row key="dashboard-modal-row-delivery-address-1">
                    <Col md={4}>
                      <label>Str:</label>&nbsp;
                      <span>{modalData.StradaResedinta}</span>
                    </Col>
                    <Col md={2}>
                      <label>Nr:</label>&nbsp;
                      <span>{modalData.NumarResedinta}</span>
                    </Col>
                    <Col md={2}>
                      <label>Bl:</label>&nbsp;
                      <span>{modalData.BlocResedinta}</span>
                    </Col>
                    <Col md={2}>
                      <label>Sc:</label>&nbsp;
                      <span>{modalData.ScaraResedinta}</span>
                    </Col>
                    <Col md={2}>
                      <label>Ap:</label>&nbsp;
                      <span>{modalData.ApartamentResedinta}</span>
                    </Col>
                  </Row>
                  <Row key="dashboard-modal-row-delivery-address-2">
                    <Col md={6}>
                      <label>Judet:</label>&nbsp;
                      <span>{modalData.JudetResedinta}</span>
                    </Col>
                    <Col md={6}>
                      <label>Localitate/Sector:</label>&nbsp;
                      <span>{modalData.LocalitateResedinta}</span>
                    </Col>
                  </Row>
                </>
              )}
              <h5 className="mt-3">Detalii Legitimare</h5>
              <Row
                key="dashboard-modal-row-collaboration-type"
                className="mt-2"
              >
                <Col md={12}></Col>
                <Col md={6}>
                  <label>An Legitimare:</label>&nbsp;
                  <span>{modalData.membershipYear}</span>
                </Col>
                <Col md={6}>
                  <label>Taxa legitimare:</label>&nbsp;
                  <span
                    className={
                      modalData.taxNotApplicable
                        ? "text-secondary"
                        : modalData.taxPaid
                        ? "text-success"
                        : "text-danger"
                    }
                  >
                    {modalData.taxNotApplicable
                      ? taxLabels.NOT_APPLICABLE
                      : modalData.taxPaid
                      ? taxLabels.PAID
                      : taxLabels.NOT_PAID}
                  </span>
                </Col>
              </Row>
              {modalData.subscriptions?.map(
                (
                  {
                    location,
                    subject,
                    course,
                    enrollmentYear,
                    admissionYear,
                    graduationYear,
                    collaborationType,
                    blocked,
                    graduated,
                  },
                  idx
                ) => (
                  <Row key={`dashboard-modal-row-subscription-${idx}`}>
                    <Col md={12}>
                      <h5 className="mt-3">Inrolare nr {idx + 1}</h5>
                    </Col>
                    <Col md={12}>
                      <label>Locatie:</label>&nbsp;
                      <span>{location?.name}</span>
                    </Col>
                    <Col md={12}>
                      <label>Disciplina:</label>&nbsp;
                      <span>{subject?.name}</span>
                    </Col>
                    <Col md={12}>
                      <label>Curs:</label>&nbsp;
                      <span>{course?.name}</span>
                    </Col>
                    <Col md={12}>
                      <label>Status curs:</label>&nbsp;
                      <span
                        className={
                          blocked || graduated ? "text-danger" : "text-success"
                        }
                      >
                        {blocked || graduated ? statusLabels.INACTIVE : statusLabels.ACTIVE}
                      </span>
                    </Col>
                    <Col md={6}>
                      <label>Tip Colaborare:</label>&nbsp;
                      <span>{collaborationTypesLabels[collaborationType]}</span>
                    </Col>
                    <Col md={6}>
                      <label>An Inrolare:</label>&nbsp;
                      <span>{enrollmentYear}</span>
                    </Col>
                    <Col md={6}>
                      <label>An Admitere:</label>&nbsp;
                      <span>{admissionYear}</span>
                    </Col>
                    <Col md={6}>
                      <label>An Absolvire:</label>&nbsp;
                      <span>{graduationYear}</span>
                    </Col>
                  </Row>
                )
              )}
              <h5 className="mt-3">Comentarii</h5>
              <Row key="dashboard-modal-row-activity-comment">
                <Col md={12}>
                  {isAdmin ? (
                    <Input
                      id="activityComment"
                      name="activityComment"
                      type="textarea"
                      value={activityComment}
                      onChange={onActivityCommentChange}
                    />
                  ) : (
                    <pre>{modalData.activityComment}</pre>
                  )}
                </Col>
              </Row>
            </>
          ) : (
            <div className="text-center">
              <span>Fara rezultat</span>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between w-100">
          {isAdmin ? (
            <Button
              color="primary"
              onClick={submitEditActivityComment.bind(
                null,
                modalData?._id,
                activityComment
              )}
            >
              Salvare
            </Button>
          ) : (
            <span></span>
          )}
          <Button color="secondary" onClick={toggleModal}>
            Inchidere
          </Button>
        </ModalFooter>
      </Modal>
      <Row className="mt-5">
        <Col className="d-flex justify-content-between align-items-center">
          <span className="">
            <h3>Dashboard ({totalCount})</h3>
          </span>
        </Col>
      </Row>
      <Row className="mt-1 justify-content-between">
        <Col className="col-10 d-inline-flex justify-content-start">
          <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="enrollmentYear"
              placeholder="An inscriere"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                yearsOptions.find(
                  ({ value }) => value === filters["enrollmentYear"]
                ) || null
              }
              options={yearsOptions}
            />
          </InputGroup>
          <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="tax"
              placeholder="Taxa"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                taxOptions.find(({ value }) => value === filters["tax"]) || null
              }
              options={taxOptions}
            />
          </InputGroup>
          <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="location"
              placeholder="Locatie"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                locationsOptions.find(
                  ({ value }) => value === filters["location"]
                ) || null
              }
              options={locationsOptions}
            />
          </InputGroup>
          <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="subject"
              placeholder="Disciplina"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                subjectsOptions.find(
                  ({ value }) => value === filters["subject"]
                ) || null
              }
              options={subjectsOptions}
            />
          </InputGroup>
          <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="course"
              placeholder="Curs"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                coursesOptions.find(
                  ({ value }) => value === filters["course"]
                ) || null
              }
              options={coursesOptions}
            />
          </InputGroup>
          <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="collaborationType"
              placeholder="Colaborare"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                collaborationTypesOptions.find(
                  ({ value }) => value === filters["collaborationType"]
                ) || null
              }
              options={collaborationTypesOptions}
            />
          </InputGroup>
          <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="status"
              placeholder="Status curs"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                statusOptions.find(({ value }) => value === filters["status"]) || null
              }
              options={statusOptions}
            />
          </InputGroup>
          {/* <InputGroup className="input-filters">
            <Select
              className="w-100"
              name="citizenship"
              placeholder="Cetatenie"
              noOptionsMessage={() => <span>Fara rezultat</span>}
              isClearable={true}
              onChange={handleFilters}
              value={
                citizenshipOptions.find(
                  ({ value }) => value === filters["citizenship"]
                ) || null
              }
              options={citizenshipOptions}
            />
          </InputGroup> */}
        </Col>
        <Col className="col-2 d-inline-flex">
          <InputGroup>
            <Input
              type="text"
              placeholder="Search..."
              onChange={onSearchChange}
              onKeyUp={handleSearch}
              value={searchValue}
            />
            <Button
              disabled={loadingActions || loading}
              color="secondary"
              size="sm"
              onClick={handleSearch}
            >
              <BsSearch />
            </Button>
          </InputGroup>
          <Button
            className="export-excel"
            disabled={loadingActions || loading}
            color="success"
            size="sm"
            onClick={handleExportExcel}
          >
            <BsFiletypeXlsx />
          </Button>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col style={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nume</th>
                <th>Prenume</th>
                <th>CNP / SSN</th>
                <th>Email</th>
                <th>Numar telefon</th>
                <th>Taxa legitimare</th>
                <th>Curs</th>
                <th>Disciplina</th>
                <th>Locatie</th>
                <th>Tip colaborare</th>
                <th>An inscriere</th>
                <th>Aprobare de zbor</th>
                <th>Status curs</th>
                <th>Detalii profil</th>
                <th className="text-center">Dosar</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={16} className="text-center">
                    <Spinner
                      color="dark"
                      style={{ width: "3rem", height: "3rem" }}
                    />
                  </td>
                </tr>
              ) : (
                getRows()
              )}
            </tbody>
          </Table>
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            onSetPage={setCurrentPage}
          />
        </Col>
      </Row>
    </Container>
  );
});

export default Dashboard;
